import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useDrizzleStudio } from "@-/com.npmjs.expo-drizzle-studio-plugin";
import { Platform } from "@-/com.npmjs.react-native";
import {
  createActorDatabaseConnection,
  discardCorruptActorReplica,
  getActorDatabase as getActorDatabaseFromDatabaseActor,
  getActorRawDatabase,
  markActorDatabaseConnectionClosed,
  prepareActorReplicaRebuild,
  resetActorDatabaseForRecovery,
  retireActorReplicaForRebuild,
  type ActorDatabaseConnection,
  type ActorReplicaRebuildResult,
  type ActorDatabaseHandle,
  type ActorRawDatabaseHandle,
} from "@-/dev.improve.haniverse.database.actor";
import { getRuntimeScopedState } from "@-/dev.improve.haniverse.lib.runtime-scoped-state";
import { registerBeforeRestart } from "@-/dev.improve.haniverse.lib.app.restart";
import { useCallback, useSyncExternalStore } from "react";
import {
  createActorDatabaseLifecycle,
  type ActorDatabaseLifecycle,
  type PausedActorDatabaseLifecycle,
} from "#actor-database-lifecycle";
import { ActorDatabaseUnavailableError } from "#actor-database-unavailable-error";

export type GetActorDatabase = () => Promise<ActorDatabaseHandle>;
export type GetActorDatabaseConnection = () => Promise<ActorDatabaseConnection>;
export type GetActorRawDatabase = () => Promise<ActorRawDatabaseHandle>;

interface ActorDatabaseLoadResult {
  connection: ActorDatabaseConnection;
  database: ActorDatabaseHandle;
  rawDatabase: ActorRawDatabaseHandle;
}

interface ActorDatabaseStore {
  /** Damaged replicas discarded by an open in this process: at most one, so a bad disk cannot loop. */
  corruptReplicasDiscarded: number;
  generation: number;
  generationListeners: Set<() => void>;
  lifecycle: ActorDatabaseLifecycle<ActorDatabaseConnection, ActorDatabaseLoadResult>;
}

async function openAndMigrateActorDatabase(
  own: (connection: ActorDatabaseConnection) => void,
  reportDamage: (error: unknown) => void,
): Promise<ActorDatabaseLoadResult> {
  try {
    return await openAndMigrateActorDatabaseUnwrapped(own, reportDamage);
  } catch (error) {
    // The lifecycle latches this and hands it to every later query: typed so the UI can say
    // "study data unavailable" instead of treating it as an ordinary failed request.
    throw new ActorDatabaseUnavailableError(error);
  }
}

async function openAndMigrateActorDatabaseUnwrapped(
  own: (connection: ActorDatabaseConnection) => void,
  reportDamage: (error: unknown) => void,
): Promise<ActorDatabaseLoadResult> {
  logger(import.meta.location).log("[actor-db] opening connection");

  const startedAt = performance.now();
  // A connection that stalls later (an operation outlives its deadline and may still be running on
  // the engine) reports it here, and the lifecycle latches `failed` so recovery replaces it.
  //
  // The open also migrates the database: one atomic, unlogged transaction inside the engine's
  // owner, signed in or signed out, online or offline (see `database.md`, "Device: schema
  // updater"). There is no separate migration step here, and nothing that can hold the open.
  let connection: ActorDatabaseConnection;
  try {
    connection = await createActorDatabaseConnection({ onDamaged: reportDamage });
  } catch (error) {
    // A signed-in replica that is damaged is replaced by a fresh download of the cloud copy (see
    // `discardCorruptActorReplica`); anything else, and a second damaged file, reach Settings.
    const store = getStore();
    if (store.corruptReplicasDiscarded >= 1 || !(await discardCorruptActorReplica(error))) {
      throw error;
    }
    store.corruptReplicasDiscarded += 1;
    connection = await createActorDatabaseConnection({ onDamaged: reportDamage });
  }
  own(connection);
  logger(import.meta.location).log(
    `[actor-db] connection opened in ${Math.round(performance.now() - startedAt)}ms`,
  );

  const database = getActorDatabaseFromDatabaseActor(connection);
  const rawDatabase = getActorRawDatabase(connection);
  return { connection, database, rawDatabase };
}

async function closeActorConnection(connection: ActorDatabaseConnection): Promise<void> {
  // `closeAsync()` here is the close-safe wrapper (`makeCloseSafeSqliteConnection`):
  // it flips the connection to "closing" so no new statement can start, then WAITS
  // for every in-flight native statement to drain before running the engine close.
  // That is what makes the sign-up/guest-conversion reopen safe — the Turso engine
  // can reopen the SAME actor.db without a second live SQLite engine holding the
  // file, and without freeing a statement mid-`getColumnNames()` (the native
  // `pthread_mutex_lock` SIGSEGV). A bare `SELECT 1;` barrier used to sit here but
  // couldn't cover statements a still-running JS query dispatched after it —
  // `cancelQueries()` doesn't abort already-dispatched native work.
  await connection.closeAsync();
  markActorDatabaseConnectionClosed();
}

// Runtime-scoped, not module-scoped: this store OWNS a native connection. A Fast
// Refresh re-evaluates this module; the re-evaluation must find the same store and
// the same live handle, never a fresh "closed" that would open a second one.
function getStore(): ActorDatabaseStore {
  return getRuntimeScopedState("contexts.database.actor", (): ActorDatabaseStore => {
    const lifecycle = createActorDatabaseLifecycle<
      ActorDatabaseConnection,
      ActorDatabaseLoadResult
    >({
      close: closeActorConnection,
      onError: (message, error) => {
        logger(import.meta.location).warn(`[actor-db] ${message}`, error);
      },
      open: openAndMigrateActorDatabase,
    });
    // The runtime that owns the connection must close it before it is replaced.
    registerBeforeRestart("actor-database", () => lifecycle.close());
    return {
      corruptReplicasDiscarded: 0,
      generation: 0,
      generationListeners: new Set(),
      lifecycle,
    };
  });
}

function emitActorDatabaseGeneration(): void {
  const store = getStore();
  store.generation += 1;
  for (const listener of store.generationListeners) {
    listener();
  }
}

function subscribeToActorDatabaseGeneration(listener: () => void): () => void {
  const { generationListeners } = getStore();
  generationListeners.add(listener);
  return () => {
    generationListeners.delete(listener);
  };
}

function getActorDatabaseGeneration(): number {
  return getStore().generation;
}

export function useActorDatabaseGeneration(): number {
  return useSyncExternalStore(
    subscribeToActorDatabaseGeneration,
    getActorDatabaseGeneration,
    getActorDatabaseGeneration,
  );
}

// External store over the open connection for the cached raw-database handle.
// `useGetActorDatabase` reads it via `useSyncExternalStore` instead of holding a
// `useState` set from inside `getActorDatabase`: that getter runs during
// render-phase suspense fetches (React Query invokes `queryFn` while rendering),
// so a setState in its async continuation can target a provider fiber the
// suspended render never mounted — React 19's "state update on a component that
// hasn't mounted yet" warning.
function subscribeToActorDatabaseLoad(listener: () => void): () => void {
  return getStore().lifecycle.subscribe(listener);
}

function getCachedActorRawDatabase(): ActorRawDatabaseHandle | null {
  return getCachedActorDatabase()?.rawDatabase ?? null;
}

function getActorDatabaseFailure(): unknown {
  return getStore().lifecycle.getFailure();
}

/**
 * The actor database's latched open failure, or `undefined`. Every actor query rejects while
 * this is set, so the app shell swaps in a recovery screen: no screen may present a failed open
 * as an endless loading state (a permanently disabled "Start Reviewing").
 */
export function useActorDatabaseFailure(): unknown {
  return useSyncExternalStore(
    subscribeToActorDatabaseLoad,
    getActorDatabaseFailure,
    getActorDatabaseFailure,
  );
}

export function getCachedActorDatabase(): ActorDatabaseLoadResult | undefined {
  return getStore().lifecycle.getLoaded();
}

/**
 * Publish an already-open connection as THE actor connection. For test rigs that
 * open their own fixtures; production opens go through `loadActorDatabase`.
 */
export function setCachedActorDatabase(
  result: ActorDatabaseLoadResult,
  // `emitLoadSettled: false` seeds the cache without notifying subscribers — for test
  // wrappers that seed while a previous test's tree is still mounted (the notify would
  // land outside `act()`). Trees rendered after the seed read the cache at mount.
  { emitLoadSettled = true }: { emitLoadSettled?: boolean } = {},
): void {
  getStore().lifecycle.publish(result, { notify: emitLoadSettled });
}

/**
 * Forget the current connection WITHOUT closing it. Only for test teardown, where
 * the rig closes its own fixture handle; production code closes through
 * `closeCachedActorDatabaseConnection`, which is the only path that releases
 * native ownership.
 */
export function clearCachedActorDatabase(
  // `emitLoadSettled: false` clears the cache without notifying `useSyncExternalStore`
  // subscribers. Test teardown needs this: it clears while the rendered tree is still
  // mounted, and a store notify there lands outside `act()`.
  { emitLoadSettled = true }: { emitLoadSettled?: boolean } = {},
): void {
  getStore().lifecycle.forget({ notify: emitLoadSettled });
}

export async function loadActorDatabase(): Promise<ActorDatabaseLoadResult> {
  return getStore().lifecycle.load();
}

async function loadActorDatabaseForGeneration(
  _generation: number,
): Promise<ActorDatabaseLoadResult> {
  return loadActorDatabase();
}

/**
 * Hold new actor-database opens while `operation` runs. Bounded (see the lifecycle's
 * `runWithLoadsPaused`): a load waiting behind it fails with a retryable timeout, and an operation
 * that holds too long is rejected. Inside `operation`, load or nest through `paused` — a load
 * through the ordinary getters would wait on the pause it is inside of.
 */
export async function runWithActorDatabaseLoadsPaused<T>(
  operation: (paused: PausedActorDatabaseLifecycle<ActorDatabaseLoadResult>) => Promise<T>,
): Promise<T> {
  return getStore().lifecycle.runWithLoadsPaused(operation);
}

export function useGetActorDatabase(): GetActorDatabase {
  const generation = useActorDatabaseGeneration();
  const rawDatabase = useSyncExternalStore(
    subscribeToActorDatabaseLoad,
    getCachedActorRawDatabase,
    getCachedActorRawDatabase,
  );

  const getActorDatabase = useCallback(async () => {
    const result = await loadActorDatabaseForGeneration(generation);
    return result.database;
  }, [generation]);

  useDrizzleStudio(rawDatabase !== null && Platform.OS !== "web" ? rawDatabase.$client : null);

  return getActorDatabase;
}

export function useGetActorDatabaseConnection(): GetActorDatabaseConnection {
  const generation = useActorDatabaseGeneration();
  return useCallback(async () => {
    const loaded = await loadActorDatabaseForGeneration(generation);
    return loaded.connection;
  }, [generation]);
}

export function useGetActorRawDatabase(): GetActorRawDatabase {
  const generation = useActorDatabaseGeneration();
  return useCallback(async () => {
    const loaded = await loadActorDatabaseForGeneration(generation);
    return loaded.rawDatabase;
  }, [generation]);
}

/**
 * Close the actor connection and return the lifecycle to `closed`, so the NEXT
 * `loadActorDatabase()` re-opens from scratch (in sync mode once a device-account
 * exists). This is the ONLY production path out of `open` and out of `failed`.
 *
 * A close that cannot be confirmed keeps the connection published and rethrows:
 * releasing ownership of a handle that may still be running native work is exactly
 * the replacement-writer hazard this lifecycle exists to prevent.
 *
 * Used by the in-place sign-in/sign-out swap (`reopenActorDatabaseConnectionInSyncMode`)
 * instead of a JS reload, by the error boundary's in-process recovery, and by
 * `restartApp` before the runtime is replaced.
 */
export async function closeCachedActorDatabaseConnection(): Promise<void> {
  return getStore().lifecycle.close();
}

/**
 * Leave a latched failed open. `retry` reopens the same files (a transient failure); `reset`
 * first deletes the actor database — the only way out of a quarantined one — so the next open
 * starts fresh (bootstrapped from the cloud when signed in). Callers reset queries afterwards so
 * every observer re-runs against the new connection.
 */
export async function recoverActorDatabase({ reset }: { reset: boolean }): Promise<void> {
  await runWithActorDatabaseLoadsPaused(async () => {
    await closeCachedActorDatabaseConnection();
    if (reset) {
      await resetActorDatabaseForRecovery();
    }
    emitActorDatabaseGeneration();
  });
}

/**
 * Replace the account's replica with a fresh download, carrying its unsent changes to the cloud
 * first (see `database.md`, "Device: sync supervisor and rebuild"). The export and the import run
 * while the app keeps working; only the close, a last small import of what was written meanwhile,
 * and the retirement hold the load gate. Callers reset queries afterwards so every observer
 * re-runs against the new connection, which bootstraps from the cloud copy.
 */
export async function rebuildActorDatabase(reason: string): Promise<ActorReplicaRebuildResult> {
  const live = () => getStore().lifecycle.getLoaded()?.connection ?? null;
  const prepared = await prepareActorReplicaRebuild(reason, live());
  return runWithActorDatabaseLoadsPaused(async () => {
    const result = await retireActorReplicaForRebuild(reason, prepared, {
      close: closeCachedActorDatabaseConnection,
      connection: live(),
    });
    emitActorDatabaseGeneration();
    return result;
  });
}

/**
 * Swap the actor connection from local-only → Turso sync mode IN PLACE, without a JS process
 * reload. Called from the sign-in sync-provision path AFTER the per-user DB is provisioned and
 * the device-account is stored. We rely on a reload-free swap because `Updates.reloadAsync()`
 * is broken on local staging builds (expo-updates misconfigured), which left the actor DB stuck
 * in local-only mode with an empty deck.
 *
 * Order: (1) CLOSE the live (local-only) connection through the lifecycle so two handles can
 * never sit on the same file; (2) bump the generation so every hook hands out a new getter.
 * The caller then resets the React Query cache, which re-runs the actor-data suspense query →
 * `loadActorDatabase()` → `createActorDatabaseConnection()` — now opening in sync mode because
 * `getDeviceAccount()` returns the account.
 */
export async function reopenActorDatabaseConnectionInSyncMode(): Promise<void> {
  await closeCachedActorDatabaseConnection();
  emitActorDatabaseGeneration();
}
